import BaseTableReport from 'components/atoms/base-table/report/report';
import { useTranslation } from 'react-i18next';
import { useState, type FC } from 'react';
import { useUntrackedInboundCallsFilters } from 'infrastructure/hooks/untrackedInboundCalls/useFilters';
import { useUntrackedInboundCalls } from 'infrastructure/hooks/untrackedInboundCalls/useData';
import useAlert from 'infrastructure/hooks/utils/use-alert';
import { useClearCall } from 'infrastructure/hooks/untrackedInboundCalls/useClearCall';

import { useColumns } from '../useColumns';
import AssignCallModal from '../assignCallModal';

import type { InboundCallLogs } from 'infrastructure/hooks/untrackedInboundCalls/types';

const UntrackedInboundCallsTable: FC = () => {
  const { data, isLoading, invalidate } = useUntrackedInboundCalls();
  const { filter, updateFilter } = useUntrackedInboundCallsFilters();
  const [editingItem, setEditingItem] = useState<InboundCallLogs | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(true);
  const { t } = useTranslation();
  const { Alert, showAlert } = useAlert();

  const setItemToAssign = (item: InboundCallLogs) => {
    setEditingItem(item);
    setIsModalOpen(true);
  };
  const { mutate: clearCall } = useClearCall({
    onSuccess: () => {
      invalidate();
    },
  });

  const handleClear = async (callId: string) => {
    const message = `${t('labels.untrackedInboundCallClearConfirm')}`;
    const alertAnswer = await showAlert({
      title: t('labels.untrackedInboundCallClearTitle'),
      messageTitle: message,
      successBtnLabel: t('controls.yes'),
      rejectBtnLabel: t('controls.no'),
    });

    if (alertAnswer) {
      clearCall(callId);
    }
  };

  const columns = useColumns({
    setItemToAssign,
    handleClear,
  });

  return (
    <div>
      {editingItem && isModalOpen && (
        <AssignCallModal
          onSuccess={() => {
            setEditingItem(null);
            setIsModalOpen(false);
            invalidate();
          }}
          callId={editingItem.callId}
          isOpen={isModalOpen}
          closeModal={() => {
            setEditingItem(null);
            setIsModalOpen(false);
          }}
        />
      )}
      {Alert}
      <BaseTableReport
        rowKey="id"
        isLoading={isLoading}
        columns={columns}
        headerTitle={t('labels.calls')}
        dataSource={data?.data}
        pagination={{
          current: filter.page,
          pageSize: filter.items,
          total: data?.count,
          onChange: (page) => updateFilter({ page }),
        }}
        showCount
        anchor
      />
    </div>
  );
};

export default UntrackedInboundCallsTable;
